import { useLanguage } from '../context/LanguageContext';
import {
  ArrowRight,
  ExternalLink,
  CheckCircle2,
  Calendar,
  Sparkles,
  ShoppingBag,
  Clock,
  Truck
} from 'lucide-react';
import React, { useState, lazy, Suspense } from 'react';

const CalendarModal = lazy(() => import('./CalendarModal'));

const Hero = () => {
  const { t, language } = useLanguage();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const productCards = [
    {
      id: 'catalogohub',
      name: 'Catálogo Hub',
      subtitle: language === 'es' ? 'Catálogos digitales & WhatsApp' : 'Digital Catalogs & WhatsApp',
      badge: language === 'es' ? 'Lanzado' : 'Live',
      badgeClass: 'badge-status-green',
      url: 'https://catalog.vegsoftsolutions.com/',
      img: '/assets/catalogo-hub-logo.webp',
      imgFallback: '/assets/catalogo-hub-logo.jpg',
      icon: ShoppingBag,
      tag: language === 'es' ? 'SaaS E-commerce' : 'E-commerce SaaS',
      features: language === 'es' ? ['Catálogo 3D/Web', 'Pedidos WhatsApp'] : ['3D/Web Catalog', 'WhatsApp Orders']
    },
    {
      id: 'barbershop',
      name: 'Vegsoft Barber',
      subtitle: language === 'es' ? 'Sistema para barberías' : 'Barbershop SaaS Platform',
      badge: language === 'es' ? 'Beta' : 'Beta',
      badgeClass: 'badge-status-purple',
      url: 'https://barbershop.vegsoftsolutions.com/',
      img: '/assets/barber-logo.jpg',
      imgFallback: '/assets/barber-logo.jpg',
      icon: Clock,
      tag: language === 'es' ? 'Reservas 24/7' : '24/7 Booking',
      features: language === 'es' ? ['Agenda Automática', 'Cobros Online'] : ['Auto Booking', 'Online Payments']
    },
    {
      id: 'voycargo',
      name: 'VoyCargo',
      subtitle: language === 'es' ? 'Logística y transporte' : 'Freight & Route Logistics',
      badge: language === 'es' ? 'En Desarrollo' : 'In Development',
      badgeClass: 'badge-status-amber',
      url: 'https://voycargo.cl/',
      img: '/assets/voycargo-logo.webp',
      imgFallback: '/assets/voycargo-logo.png',
      icon: Truck,
      tag: language === 'es' ? 'App & GPS Satelital' : 'Mobile App & GPS',
      features: language === 'es' ? ['Tracking Satelital', 'App Choferes'] : ['Live GPS Tracking', 'Driver App']
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <section
        id="hero"
        data-testid="hero-section"
        className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 overflow-hidden bg-[#FAFAFA]"
      >
        {/* Soft Stripe Pastel Mesh Glows */}
        <div className="mesh-glow-cyan -top-24 -left-20 animate-float-subtle" />
        <div className="mesh-glow-indigo top-0 -right-20 animate-float-subtle" style={{ animationDelay: '2.5s' }} />
        <div className="mesh-glow-lavender top-1/2 left-1/3 opacity-70" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Main Hero Header (Centered Stripe style) */}
          <div className="text-center max-w-3xl mx-auto space-y-6 mb-16">
            
            {/* Top Pill Badge */}
            <div>
              <div className="stripe-badge inline-flex">
                <span className="text-base">🚀</span>
                <span>{language === 'es' ? 'Creadores de Software & IA' : 'Software Creators & AI Studio'}</span>
              </div>
            </div>

            {/* Impact Headline */}
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] tracking-tight leading-[1.12]">
              {language === 'es' ? (
                <>
                  Creamos productos digitales que <span className="bg-gradient-to-r from-[#0F172A] via-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">transforman negocios</span>
                </>
              ) : (
                <>
                  We build digital products that <span className="bg-gradient-to-r from-[#0F172A] via-[#4F46E5] to-[#7C3AED] bg-clip-text text-transparent">transform businesses</span>
                </>
              )}
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-slate-600 font-normal leading-relaxed max-w-2xl mx-auto">
              {language === 'es'
                ? 'Impulsamos startups y empresas con soluciones SaaS innovadoras, escalables y centradas en el usuario. Desde la idea hasta el lanzamiento global.'
                : 'We empower startups and enterprises with innovative, scalable, and user-centric SaaS products. From concept to global launch.'}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 pt-2">
              <button
                onClick={() => scrollToSection('products')}
                data-testid="hero-primary-cta"
                className="w-full sm:w-auto btn-stripe-dark"
              >
                <span>{language === 'es' ? 'Descubrir Soluciones' : 'Explore Solutions'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                onClick={() => scrollToSection('process')}
                data-testid="hero-secondary-cta"
                className="w-full sm:w-auto btn-stripe-white"
              >
                <span>{language === 'es' ? 'Ver Nuestro Proceso' : 'Our Process'}</span>
              </button>
            </div>

          </div>

          {/* 3 Showcase Product Cards (Matching Mockup 2 side-by-side) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 pt-4">
            {productCards.map((product) => {
              const Icon = product.icon;
              return (
                <div
                  key={product.id}
                  className="stripe-card p-6 flex flex-col justify-between group hover:border-[#6366F1]/40"
                >
                  {/* Top Bar: Icon + Status Badge */}
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-center text-[#4F46E5] group-hover:scale-110 transition-transform">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={product.badgeClass}>
                      {product.badge}
                    </span>
                  </div>

                  {/* Visual Preview Box (Clean App UI mock preview) */}
                  <div className="rounded-xl overflow-hidden bg-slate-50 border border-slate-100 mb-5 aspect-[16/10] relative group-hover:border-slate-200 transition-colors flex items-center justify-center p-4">
                    <picture>
                      <source srcSet={product.img} type="image/webp" />
                      <img
                        src={product.imgFallback}
                        alt={product.name}
                        className="max-h-24 max-w-[170px] w-auto object-contain transform group-hover:scale-105 transition-transform duration-300 drop-shadow-sm"
                        loading="eager"
                      />
                    </picture>
                  </div>

                  {/* Info & Content */}
                  <div>
                    <h3 className="font-heading font-bold text-xl text-[#0F172A] group-hover:text-[#4F46E5] transition-colors mb-1">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-500 font-medium mb-4">
                      {product.subtitle}
                    </p>

                    {/* Features list */}
                    <div className="flex flex-wrap gap-1.5 pt-3 border-t border-slate-100">
                      {product.features.map((feat, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-50 text-slate-600 text-xs font-semibold border border-slate-100"
                        >
                          <CheckCircle2 className="w-3 h-3 text-[#10B981]" />
                          <span>{feat}</span>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Link */}
                  <div className="pt-5 mt-4 border-t border-slate-100 flex items-center justify-between">
                    <a
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold text-[#4F46E5] group-hover:text-[#3730A3] flex items-center gap-1.5 transition-colors"
                    >
                      <span>{language === 'es' ? 'Ver en vivo' : 'Live demo'}</span>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                    <span className="text-xs font-semibold text-slate-400">
                      {product.tag}
                    </span>
                  </div>

                </div>
              );
            })}
          </div>

        </div>
      </section>

      {isCalendarOpen && (
        <Suspense fallback={null}>
          <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
        </Suspense>
      )}
    </>
  );
};

export default Hero;
